/**
 * @fileoverview Unified job timeline combining system and user milestones
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { SystemMilestoneItem } from './SystemMilestoneItem';
import { UserMilestoneCard } from './UserMilestoneCard';
import { useUnifiedMilestones } from '@/hooks/useUnifiedMilestones';
import { Milestone } from '@/lib/types/milestone';

interface UnifiedJobTimelineProps {
  jobId: string;
  jobPublicId: string;
  currentUserRole: 'client' | 'vendor';
  isFullyApproved: boolean;
  canEdit: boolean;
  canRequestChange: boolean;
  onEditMilestone?: (milestone: Milestone) => void;
  onMarkComplete?: (milestoneId: string) => void;
  onRequestChange?: (milestone: Milestone) => void;
  onAddMilestone?: () => void;
  onApplyTemplate?: () => void;
  onViewMilestones?: () => void;
  needsMilestoneApproval?: boolean;
  jobStatus?: string;
}

export function UnifiedJobTimeline({
  jobId,
  jobPublicId,
  currentUserRole,
  isFullyApproved,
  canEdit,
  canRequestChange,
  onEditMilestone,
  onMarkComplete,
  onRequestChange,
  onAddMilestone,
  onApplyTemplate,
  onViewMilestones,
  needsMilestoneApproval = false,
  jobStatus,
}: UnifiedJobTimelineProps) {
  const {
    milestonesByPhase,
    currentUserMilestone,
    upcomingUserMilestones,
    progressPercentage,
    completedCount,
    totalCount,
    isLoading,
  } = useUnifiedMilestones(jobId);

  // Initialize collapsible states (all hooks must be called before any early return)
  const [hasInitialized, setHasInitialized] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [awardOpen, setAwardOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);

  // Calculate derived values with safe defaults for when data is loading
  const setupMilestones = milestonesByPhase?.setup || [];
  const awardMilestones = milestonesByPhase?.award || [];
  const completionMilestones = milestonesByPhase?.completion || [];
  const userMilestones = milestonesByPhase?.project || [];

  const setupCompleted = setupMilestones.filter(m => m.is_completed).length;
  const awardCompleted = awardMilestones.filter(m => m.is_completed).length;
  const completionCompleted = completionMilestones.filter(m => m.is_completed).length;
  const projectCompleted = userMilestones.filter(m => m.is_completed).length;

  // Determine active phase (first phase with incomplete milestones)
  const activePhase = useMemo(() => {
    if (setupCompleted < setupMilestones.length) return 'setup';
    if (awardCompleted < awardMilestones.length) return 'award';
    // Project phase is active if there are incomplete user milestones
    if (userMilestones.length > 0 && projectCompleted < userMilestones.length) return 'project';
    // Completion phase is active if there are incomplete completion milestones
    if (completionCompleted < completionMilestones.length) return 'completion';
    // All complete - default to completion (last phase)
    return 'completion';
  }, [setupMilestones.length, awardMilestones.length, completionMilestones.length, userMilestones.length, setupCompleted, awardCompleted, projectCompleted, completionCompleted]);

  // Set initial open states based on active phase (only on first load)
  useEffect(() => {
    if (!isLoading && !hasInitialized) {
      setSetupOpen(activePhase === 'setup');
      setAwardOpen(activePhase === 'award');
      setProjectOpen(activePhase === 'project');
      setCompletionOpen(activePhase === 'completion');
      setHasInitialized(true);
    }
  }, [isLoading, hasInitialized, activePhase]);

  // Now safe to do early return after all hooks are declared
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Timeline...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      {/* Progress Header */}
      <CardHeader className="py-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle>Job Timeline & Progress</CardTitle>
            <p className="text-sm text-muted-foreground">
              {completedCount} of {totalCount} milestones completed
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary">{progressPercentage}%</div>
          </div>
        </div>
        <Progress value={progressPercentage} className="h-3 mt-4" />
      </CardHeader>

      <CardContent className="space-y-1 pt-0">
        {/* Setup Phase */}
        <Collapsible open={setupOpen} onOpenChange={setSetupOpen}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 py-3 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
              {setupOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-semibold text-sm">Setup Phase</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {setupCompleted}/{setupMilestones.length} complete
              </span>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 pb-2 space-y-1">
              {setupMilestones.map(milestone => (
                <SystemMilestoneItem key={milestone.id} milestone={milestone} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Award Phase */}
        <Collapsible open={awardOpen} onOpenChange={setAwardOpen}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 py-3 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
              {awardOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-semibold text-sm">Award Phase</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {awardCompleted}/{awardMilestones.length} complete
              </span>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 pb-2 space-y-1">
              {awardMilestones.map(milestone => (
                <SystemMilestoneItem key={milestone.id} milestone={milestone} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Project Milestones Section */}
        <Collapsible open={projectOpen} onOpenChange={setProjectOpen}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 py-3 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
              {projectOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-semibold text-sm">Project Milestones</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {userMilestones.filter(m => m.is_completed).length}/{userMilestones.length} complete
              </span>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 pb-2">
              <div className="flex items-center justify-end gap-2 mb-3">
                {onViewMilestones && (jobStatus === 'awarded' || jobStatus === 'in_progress' || jobStatus === 'completed_pending_review') && (
                  <Button 
                    variant={needsMilestoneApproval ? "default" : "outline"}
                    size="sm"
                    onClick={onViewMilestones}
                    className={needsMilestoneApproval ? "relative" : jobStatus === 'in_progress' ? "bg-green-600 hover:bg-green-700 text-white relative" : ""}
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    Milestones
                    {needsMilestoneApproval && (
                      <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-orange-500 animate-pulse" />
                    )}
                  </Button>
                )}
                {onAddMilestone && (
                  <Button variant="outline" size="sm" onClick={onAddMilestone}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Milestone
                  </Button>
                )}
                {onApplyTemplate && (
                  <Button variant="outline" size="sm" onClick={onApplyTemplate}>
                    Apply Template
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {userMilestones.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <p>No custom milestones yet.</p>
                    <p className="text-sm mt-1">Add milestones to track project deliverables.</p>
                  </div>
                ) : (
                  <>
                    {/* Completed Milestones */}
                    {userMilestones.filter(m => m.is_completed).length > 0 && (
                      <div>
                        <h3 className="text-xs font-medium text-muted-foreground mb-2">Completed</h3>
                        <div className="space-y-2">
                          {userMilestones.filter(m => m.is_completed).map(milestone => (
                            <UserMilestoneCard
                              key={milestone.id}
                              milestone={milestone}
                              jobId={jobId}
                              jobPublicId={jobPublicId}
                              canEdit={false}
                              canRequestChange={false}
                              canMarkComplete={false}
                              canUploadFiles={false}
                              isFullyApproved={isFullyApproved}
                              onEdit={onEditMilestone}
                              onMarkComplete={onMarkComplete}
                              onRequestChange={onRequestChange}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Current Milestone */}
                    {currentUserMilestone && (
                      <div>
                        <h3 className="text-xs font-medium text-muted-foreground mb-2">Current Milestone</h3>
                        <UserMilestoneCard
                          milestone={currentUserMilestone}
                          jobId={jobId}
                          jobPublicId={jobPublicId}
                          canEdit={canEdit}
                          canRequestChange={canRequestChange}
                          canMarkComplete={currentUserMilestone.action_owner === currentUserRole}
                          canUploadFiles={currentUserMilestone.action_owner === currentUserRole}
                          isFullyApproved={isFullyApproved}
                          onEdit={onEditMilestone}
                          onMarkComplete={onMarkComplete}
                          onRequestChange={onRequestChange}
                        />
                      </div>
                    )}

                    {/* Upcoming Milestones */}
                    {upcomingUserMilestones.length > 0 && (
                      <div>
                        <h3 className="text-xs font-medium text-muted-foreground mb-2">Upcoming</h3>
                        <div className="space-y-2">
                          {upcomingUserMilestones.map(milestone => (
                            <div key={milestone.id} className="text-sm p-2 border rounded-lg">
                              <div className="font-medium">{milestone.milestone_name}</div>
                              <div className="text-muted-foreground text-xs mt-1">
                                Due: {new Date(milestone.due_date).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* View All Link */}
                    <Link to={`/jobs/${jobId}/milestones`}>
                      <Button variant="ghost" className="w-full">
                        View All Milestones <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Completion Phase */}
        <Collapsible open={completionOpen} onOpenChange={setCompletionOpen}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 py-3 px-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
              {completionOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-semibold text-sm">Completion Phase</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {completionCompleted}/{completionMilestones.length} complete
              </span>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-2 pb-2 space-y-1">
              {completionMilestones.map(milestone => (
                <SystemMilestoneItem key={milestone.id} milestone={milestone} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
